import { createHttpClient } from '@shared/adapters/HttpClient';
import { useToast } from '@shared/hooks/use-toast';
import { apiClientWH } from '@shared/services/ApiClient';
import type { BatchSummary } from '@shared/models/BatchSummary';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LocationsApiAdapter } from '../adapters/LocationsApiAdapter';
import {
  filterLinesCompatibleWithLocation,
  getLocationBatchLabel,
  isBatchCompatibleWithLocation,
  type LocationStockBatchRef,
} from '../utils/locationBatchCompatibility';
import { useLocateReturned } from './useLocateReturned';

interface SuggestedLocation {
  id: number;
  code: string;
  stock: number;
  type_id: number;
}

interface ProductToLocate {
  id: string;
  product_id: number; // ID del producto para llamadas API
  name: string;
  code: string;
  quantity: number;
  totalQuantity: number;
  location?: string;
  image?: string;
  batch_id?: number | null;
  batch?: BatchSummary | null;
  suggested_locations?: SuggestedLocation[];
  other_locations?: SuggestedLocation[];
}

interface LocatedProduct {
  id: string;
  product_id?: number;
  name: string;
  code: string;
  quantity: number;
  totalQuantity: number;
  location: string;
  image?: string;
  batch_id?: number | null;
  batch?: BatchSummary | null;
}

interface UseLocateLogicProps {
  returnedId: number;
  processedProducts?: any[];
  processedProductsQuantities?: Record<string, number>;
  processedProductsTotals?: Record<string, number>;
}

/** Movimientos de la línea de orden (lote/linea); si el backend no manda `location_order_line_id`, se usa el array entero. */
function movementsForLine(lineId: string | number, movements: any[] | null | undefined): any[] {
  if (!movements?.length) return [];
  const n = Number(lineId);
  if (Number.isNaN(n)) return movements;
  const scoped = movements.filter((m) => m?.location_order_line_id != null && Number(m.location_order_line_id) === n);
  return scoped.length > 0 ? scoped : movements;
}

export const useLocateLogic = ({
  returnedId,
  processedProducts = [],
  processedProductsQuantities = {},
  processedProductsTotals = {},
}: UseLocateLogicProps) => {
  const { toast } = useToast();
  const { t } = useTranslation('locationordersDetailModule');

  const pendingQtyFor = useCallback(
    (p: any) => {
      if (p == null) return 0;
      if (p.quantity != null) return p.quantity;
      return (
        (p.id != null ? processedProductsQuantities[String(p.id)] : undefined) ??
        (p.code != null ? processedProductsQuantities[p.code] : undefined) ??
        0
      );
    },
    [processedProductsQuantities],
  );

  const plannedTotalFor = useCallback(
    (p: any) => {
      if (p == null) return 0;
      if (p.totalQuantity != null) return p.totalQuantity;
      return (
        (p.id != null ? processedProductsTotals[String(p.id)] : undefined) ??
        (p.code != null ? processedProductsTotals[p.code] : undefined) ??
        0
      );
    },
    [processedProductsTotals],
  );

  // 🎯 Hook para ubicar productos
  const { locateProduct, isLocating } = useLocateReturned(returnedId);

  // 🎯 Adaptador para ubicaciones
  const locationsAdapter = useMemo(() => new LocationsApiAdapter(createHttpClient(apiClientWH)), []);

  // 🎯 Estado inicial vacío
  const [productsToLocate, setProductsToLocate] = useState<ProductToLocate[]>([]);

  // 🎯 Estado para la ubicación escaneada actual
  const [currentLocation, setCurrentLocation] = useState<{
    id: number;
    code: string;
    stock_batches?: LocationStockBatchRef[];
  } | null>(null);

  // 🎯 Estado de loading para la validación de ubicación
  const [isValidatingLocation, setIsValidatingLocation] = useState<boolean>(false);

  // 🎯 Estado para el producto siendo ubicado
  const [productBeingLocated, setProductBeingLocated] = useState<ProductToLocate | null>(null);

  // 🎯 Estado para la cantidad a ubicar
  const [quantityToLocate, setQuantityToLocate] = useState<number>(0);

  /** Líneas candidatas al escanear un SKU repetido (distinto lote). Null = lote ya definido por fila o única coincidencia. */
  const [batchSelectionCandidates, setBatchSelectionCandidates] = useState<ProductToLocate[] | null>(null);
  const [selectedBatchLineId, setSelectedBatchLineId] = useState<string | null>(null);

  // 🎯 Ref para controlar la inicialización y evitar bucles infinitos
  const initRef = useRef<{ initialized: boolean; returnedId: number | null; lastProcessedLength: number }>({
    initialized: false,
    returnedId: null,
    lastProcessedLength: 0,
  });

  useEffect(() => {
    const shouldInit =
      !initRef.current.initialized ||
      initRef.current.returnedId !== returnedId ||
      initRef.current.lastProcessedLength !== processedProducts.length;

    if (!shouldInit || processedProducts.length === 0) return;

    // 🎯 Filtrar productos por ubicar: solo los que tienen qty_pending > 0
    const productsToLocateFiltered = processedProducts.filter((product) => pendingQtyFor(product) > 0);

    const newProductsToLocate = productsToLocateFiltered.map((product, index) => ({
      id: String(product.id ?? `product-${index}`),
      product_id: product.product_id, // ID del producto para llamadas API
      name: product.name || `Producto ${product.code}`,
      code: product.code,
      quantity: pendingQtyFor(product),
      totalQuantity: plannedTotalFor(product),
      location: undefined,
      image: product.image || '/imagen/example.webp',
      batch_id: product.batch_id ?? null,
      batch: product.batch ?? null,
      // Ubicaciones sugeridas que vienen directamente de la API
      suggested_locations: product.suggested_locations || [],
      other_locations: product.other_locations || [],
    }));

    setProductsToLocate(newProductsToLocate);

    // 🎯 Líneas ya completas (incl. con lote): qty_moved = qty_planned, una fila por id de línea
    // No exigir movements: a veces la API devuelve null aun con línea completa; la ubicación es opcional.
    const productsAlreadyLocatedFromAPI = processedProducts
      .filter((product) => {
        const qtyMoved = product.processedQuantity ?? 0;
        const qtyPlanned = product.totalQuantity ?? 0;
        return qtyMoved === qtyPlanned && qtyPlanned > 0;
      })
      .map((product) => {
        const lineMovements = movementsForLine(product.id, product.movements);
        const lastMovement = lineMovements[lineMovements.length - 1];
        const locationCode =
          lastMovement?.location?.code ||
          (lastMovement?.to_location_id != null ? `Location ${lastMovement.to_location_id}` : '');

        return {
          id: String(product.id),
          product_id: product.product_id,
          name: product.name || `Producto ${product.code}`,
          code: product.code,
          quantity: product.processedQuantity ?? 0, // qty_moved
          totalQuantity: product.totalQuantity ?? 0, // qty_planned
          location: locationCode || t('ui.located_line_no_location', { defaultValue: '—' }),
          image: product.image || '/imagen/example.webp',
          batch_id: product.batch_id ?? null,
          batch: product.batch ?? null,
        };
      });

    // 🎯 Combinar productos ya ubicados de la API con los ubicados en esta sesión
    // Usar función de actualización para acceder al estado anterior
    setLocatedProducts((prevLocatedProducts) => {
      // Una línea de orden por id (mismo producto puede repetirse con distinto lote)
      const locatedProductsMap = new Map<string, LocatedProduct>();

      // Primero agregar los de la sesión actual (tienen prioridad)
      prevLocatedProducts.forEach((product) => {
        locatedProductsMap.set(product.id, product);
      });

      // Luego agregar los de la API (solo si no están ya en la sesión actual)
      productsAlreadyLocatedFromAPI.forEach((product) => {
        if (!locatedProductsMap.has(product.id)) {
          locatedProductsMap.set(product.id, product);
        } else {
          // Si ya existe, actualizar con los datos más recientes de la API
          const existing = locatedProductsMap.get(product.id)!;
          // Actualizar cantidad si la de la API es mayor (más reciente)
          if (product.quantity > existing.quantity) {
            locatedProductsMap.set(product.id, product);
          }
        }
      });

      return Array.from(locatedProductsMap.values());
    });

    // Actualizar el ref
    initRef.current = {
      initialized: true,
      returnedId,
      lastProcessedLength: processedProducts.length,
    };
  }, [processedProducts, pendingQtyFor, plannedTotalFor, returnedId, t]);

  const [locatedProducts, setLocatedProducts] = useState<LocatedProduct[]>([]);
  const [toLocateSortBy, setToLocateSortBy] = useState<string>('more_qty');
  const [locatedSortBy, setLocatedSortBy] = useState<string>('more_qty');

  const showBatchIncompatibleToast = useCallback(
    (productId: number) => {
      const batchLabel = getLocationBatchLabel(currentLocation?.stock_batches, productId);
      toast({
        title: t('toasts.batch_incompatible_with_location.title', { defaultValue: 'Lote incompatible' }),
        description: t('toasts.batch_incompatible_with_location.description', {
          defaultValue: batchLabel
            ? 'Esta ubicación ya tiene el lote {{batch}}. No puedes ubicar un lote distinto.'
            : 'Esta ubicación ya tiene un lote asignado. No puedes ubicar un lote distinto.',
          batch: batchLabel ?? '',
        }),
        variant: 'destructive',
      });
    },
    [currentLocation?.stock_batches, toast, t],
  );

  const beginProductLocate = useCallback(
    (candidates: ProductToLocate[]) => {
      const compatibleCandidates = filterLinesCompatibleWithLocation(candidates, currentLocation?.stock_batches);

      if (compatibleCandidates.length === 0) {
        showBatchIncompatibleToast(candidates[0]?.product_id ?? 0);
        return;
      }

      if (compatibleCandidates.length === 1) {
        setBatchSelectionCandidates(null);
        setSelectedBatchLineId(null);
        setProductBeingLocated(compatibleCandidates[0]);
        setQuantityToLocate(1);
        toast({
          title: t('toasts.unit_added.title'),
          description: t('toasts.unit_added.description'),
          variant: 'default',
        });
        return;
      }

      setBatchSelectionCandidates(compatibleCandidates);
      setSelectedBatchLineId(null);
      setProductBeingLocated(compatibleCandidates[0]);
      setQuantityToLocate(1);
    },
    [currentLocation?.stock_batches, showBatchIncompatibleToast, toast, t],
  );

  // 🎯 Función para escanear ubicación o producto
  const handleLocationScan = useCallback(
    async (code: string) => {
      // Si ya hay una ubicación, buscar el producto
      if (currentLocation) {
        // Misma referencia de producto puede repetirse en varias líneas (distinto lote)
        const candidates = productsToLocate.filter((p) => p.code === code);

        if (candidates.length >= 1) {
          beginProductLocate(candidates);
        } else {
          // Mismo SKU en varias líneas/lotes: hay que distinguir en «Por ubicar»; aquí, si no queda nada por ubicar, el código es «ya completado».
          const alreadyLocated = locatedProducts.find(
            (p) => p.code === code && p.quantity > 0 && p.quantity === p.totalQuantity,
          );
          if (alreadyLocated) {
            toast({
              title: t('toasts.product_already_located.title'),
              description: t('toasts.product_already_located.description', {
                product: alreadyLocated.name,
              }),
              variant: 'destructive',
            });
          } else {
            toast({
              title: t('toasts.product_not_found.title'),
              description: t('toasts.product_not_found.description', { code }),
              variant: 'destructive',
            });
          }
        }
        return;
      }

      // Si no hay ubicación, validar la ubicación escaneada
      setIsValidatingLocation(true);

      try {
        // Validar ubicación con la API
        const response = await locationsAdapter.getLocationByCode(code);

        if (response.success && response.data) {
          // Ubicación válida - guardar en el estado
          setCurrentLocation({
            id: response.data.id,
            code: response.data.code,
            stock_batches: response.data.stock_batches,
          });
          // ✅ Sin toast de confirmación para evitar confusión
        } else {
          // Ubicación no encontrada - mostrar error
          toast({
            title: t('toasts.location_error.title'),
            description: response.message || t('toasts.location_error.description'),
            variant: 'destructive',
          });
        }
      } catch {
        // Error inesperado
        toast({
          title: t('toasts.location_error.title'),
          description: t('toasts.location_error.unexpected'),
          variant: 'destructive',
        });
      } finally {
        setIsValidatingLocation(false);
      }
    },
    [currentLocation, productsToLocate, locatedProducts, beginProductLocate, toast, t, locationsAdapter],
  );

  // 🎯 Función para escanear unidades (código del producto)
  const handleScanUnits = useCallback(
    (scannedCode: string) => {
      if (!productBeingLocated) return;

      if (batchSelectionCandidates && batchSelectionCandidates.length > 1 && !selectedBatchLineId) {
        toast({
          title: t('toasts.select_batch_first.title', { defaultValue: 'Selecciona un lote' }),
          description: t('toasts.select_batch_first.description', {
            defaultValue: 'Hay varias líneas con este producto. Elige el lote antes de continuar.',
          }),
          variant: 'default',
        });
        return;
      }

      // Verificar si el código escaneado coincide con el producto actual
      if (scannedCode !== productBeingLocated.code) {
        toast({
          title: t('toasts.wrong_product_code.title'),
          description: t('toasts.wrong_product_code.description'),
          variant: 'destructive',
        });
        return;
      }

      // Si es el código correcto, sumar 1 unidad
      // Usar totalQuantity como máximo (qty_planned) en lugar de quantity (qty_pending)
      const maxQuantity = productBeingLocated.totalQuantity || productBeingLocated.quantity;
      const newQuantity = Math.min(quantityToLocate + 1, maxQuantity);
      setQuantityToLocate(newQuantity);

      // Mostrar toast informativo
      if (newQuantity >= productBeingLocated.quantity) {
        toast({
          title: t('toasts.max_quantity_reached.title'),
          description: t('toasts.max_quantity_reached.description'),
          variant: 'destructive',
        });
      } else {
        toast({
          title: t('toasts.unit_added.title'),
          description: t('toasts.unit_added.description'),
          variant: 'default',
        });
      }
    },
    [productBeingLocated, quantityToLocate, batchSelectionCandidates, selectedBatchLineId, toast, t],
  );

  const handleBatchLineSelect = useCallback(
    (lineId: string) => {
      const line = batchSelectionCandidates?.find((c) => c.id === lineId);
      if (!line) return;
      setSelectedBatchLineId(lineId);
      setProductBeingLocated(line);
      setQuantityToLocate(1);
    },
    [batchSelectionCandidates],
  );

  // 🎯 Función para ubicar un producto
  const handleProductLocate = useCallback(
    (productId: string, locationId: number, _productCode?: string, batchId?: number) => {
      const productIndex = productsToLocate.findIndex((p) => String(p.id) === String(productId));
      if (productIndex === -1 || !currentLocation) return;

      const product = productsToLocate[productIndex];
      const qtyToLocate = quantityToLocate || 1;
      const effectiveBatchId = batchId ?? product.batch_id ?? undefined;

      if (!isBatchCompatibleWithLocation(effectiveBatchId ?? null, product.product_id, currentLocation.stock_batches)) {
        showBatchIncompatibleToast(product.product_id);
        return;
      }

      const payload: {
        product_id: number;
        qty: number;
        to_location_id: number;
        batch_id?: number;
      } = {
        product_id: product.product_id,
        qty: qtyToLocate,
        to_location_id: locationId,
      };
      if (effectiveBatchId != null) {
        payload.batch_id = effectiveBatchId;
      }

      // 🎯 Llamar a la API para registrar el movimiento
      locateProduct(payload, {
        onSuccess: () => {
          // 🎯 Calcular la cantidad total movida después de esta ubicación
          // Buscar el producto en processedProducts para obtener qty_moved actual del backend
          const processedProduct = processedProducts.find((p) => String(p.id) === String(productId));
          const qtyMovedBefore = processedProduct?.processedQuantity ?? 0;
          // La cantidad total movida después de esta ubicación será la del backend + lo que acabo de ubicar
          // Nota: El backend actualizará qty_moved, pero aquí calculamos localmente para verificar si está completo
          const totalQtyMovedAfterLocate = qtyMovedBefore + qtyToLocate;
          const qtyPlanned = product.totalQuantity;

          // 🎯 Solo agregar a ubicados si está completado (qty_planned = qty_moved)
          const isCompleted = totalQtyMovedAfterLocate === qtyPlanned && qtyPlanned > 0;

          // Remover o actualizar en productos por ubicar según cantidad pendiente
          setProductsToLocate((prev) =>
            prev.flatMap((p) => {
              const isTarget = String(p.id) === String(productId);

              if (!isTarget) return [p];

              const remainingQuantity = Math.max((p.quantity ?? 0) - qtyToLocate, 0);
              if (remainingQuantity === 0) return [];

              return [
                {
                  ...p,
                  quantity: remainingQuantity,
                },
              ];
            }),
          );

          // Solo agregar a ubicados si está completado
          if (isCompleted) {
            const locatedProduct: LocatedProduct = {
              id: product.id,
              product_id: product.product_id,
              name: product.name,
              code: product.code,
              quantity: totalQtyMovedAfterLocate, // qty_moved total
              totalQuantity: qtyPlanned, // qty_planned
              location: currentLocation.code,
              image: product.image,
              batch_id: product.batch_id ?? null,
              batch: product.batch ?? null,
            };
            setLocatedProducts((prev) => {
              const existingIndex = prev.findIndex((p) => p.id === product.id);
              if (existingIndex >= 0) {
                const updated = [...prev];
                updated[existingIndex] = locatedProduct;
                return updated;
              }
              return [...prev, locatedProduct];
            });
          }

          // 🎯 Limpiar estado para volver al paso 1
          setProductBeingLocated(null);
          setQuantityToLocate(0);
          setCurrentLocation(null);
          setBatchSelectionCandidates(null);
          setSelectedBatchLineId(null);
        },
        onError: () => {
          // El error ya se maneja en el hook, solo no actualizamos el estado local
        },
      });
    },
    [productsToLocate, quantityToLocate, currentLocation, locateProduct, processedProducts, showBatchIncompatibleToast],
  );

  // 🎯 Función para limpiar ubicación manualmente
  const handleClearLocation = useCallback(() => {
    setCurrentLocation(null);
    setProductBeingLocated(null);
    setQuantityToLocate(0);
    setBatchSelectionCandidates(null);
    setSelectedBatchLineId(null);

    toast({
      title: t('toasts.location_cleared.title'),
      description: t('toasts.location_cleared.description'),
      variant: 'default',
    });
  }, [toast, t]);

  // 🎯 Función para finalizar ubicación
  const handleFinishLocated = useCallback(() => {
    if (productsToLocate.length > 0) {
      toast({
        title: t('toasts.finish_warning.title'),
        description: t('toasts.finish_warning.description'),
        variant: 'destructive',
      });
      return;
    }

    // 🎯 Validar que todas las líneas tengan qty_planned = qty_moved antes de considerar completado
    const allLinesCompleted = processedProducts.every((product) => {
      const qtyMoved = product.processedQuantity ?? 0;
      const qtyPlanned = product.totalQuantity ?? 0;
      return qtyMoved === qtyPlanned && qtyPlanned > 0;
    });

    if (!allLinesCompleted) {
      toast({
        title: t('toasts.finish_warning.title'),
        description:
          t('toasts.finish_warning.description') ||
          'No se puede finalizar: todas las líneas deben tener qty_planned = qty_moved',
        variant: 'destructive',
      });
      return;
    }

    // 🎯 Los movimientos ya se registraron individualmente al hacer clic en "Ubicar"
    // Solo mostramos un mensaje de éxito
    toast({
      title: t('toasts.finish_success.title'),
      description: t('toasts.finish_success.description'),
      variant: 'default',
    });
  }, [toast, t, productsToLocate.length, processedProducts]);

  // 🎯 Funciones de ordenamiento
  const handleToLocateSort = useCallback((sortBy: string) => {
    setToLocateSortBy(sortBy);
  }, []);

  const handleLocatedSort = useCallback((sortBy: string) => {
    setLocatedSortBy(sortBy);
  }, []);

  const getPrimaryLocationCode = useCallback((product: ProductToLocate) => {
    const suggested = product.suggested_locations ?? [];
    return suggested[0]?.code ?? '';
  }, []);

  // 🎯 Productos ordenados
  const sortedProductsToLocate = useMemo(() => {
    return [...productsToLocate].sort((a, b) => {
      switch (toLocateSortBy) {
        case 'more_qty':
          return b.quantity - a.quantity;
        case 'less_qty':
          return a.quantity - b.quantity;
        case 'name_az':
          return a.name.localeCompare(b.name);
        case 'name_za':
          return b.name.localeCompare(a.name);
        case 'location_az':
          return getPrimaryLocationCode(a).localeCompare(getPrimaryLocationCode(b));
        case 'location_za':
          return getPrimaryLocationCode(b).localeCompare(getPrimaryLocationCode(a));
        default:
          return 0;
      }
    });
  }, [productsToLocate, toLocateSortBy, getPrimaryLocationCode]);

  const sortedLocatedProducts = useMemo(() => {
    return [...locatedProducts].sort((a, b) => {
      switch (locatedSortBy) {
        case 'more_qty':
          return b.quantity - a.quantity;
        case 'less_qty':
          return a.quantity - b.quantity;
        case 'name_az':
          return a.name.localeCompare(b.name);
        case 'name_za':
          return b.name.localeCompare(a.name);
        default:
          return 0;
      }
    });
  }, [locatedProducts, locatedSortBy]);

  return {
    productsToLocate: sortedProductsToLocate,
    locatedProducts: sortedLocatedProducts,
    toLocateSortBy,
    locatedSortBy,
    currentLocation,
    isValidatingLocation,
    productBeingLocated,
    quantityToLocate,
    batchSelectionCandidates,
    selectedBatchLineId,
    setProductBeingLocated,
    setQuantityToLocate,
    handleLocationScan,
    handleScanUnits,
    handleBatchLineSelect,
    handleProductLocate,
    handleClearLocation,
    handleToLocateSort,
    handleLocatedSort,
    handleFinishLocated,
    isLocating,
  };
};
